import { useState, type CSSProperties } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { FormaPagamento } from '@arena/shared/dominio';
import {
  ROTULO_FORMA_PAGAMENTO,
  zRealizacaoDeLancamento,
  type DetalheDoLancamento,
  type EscopoDeCancelamento,
  type RealizacaoDeLancamento,
  type ResultadoDeCancelamento,
  type ResultadoDeEstorno,
} from '@arena/shared/schemas';
import { dinheiro, paraCentavos } from '../agenda/formatos.js';
import { dataCompleta, errosPorCampo, primeiraMensagem } from '../alunos/formatos.js';
import { ErroDeApi, mensagemDoErro } from '../api/cliente.js';
import { financeiroApi } from '../api/financeiro.js';
import { invalidarDinheiro } from '../api/invalidacoes.js';
import { Icone } from '../ui/Icone.js';
import { Botao, Campo, Escolhas } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import {
  corpoDaRealizacao,
  dataPadraoDaRealizacao,
  erroDaDataDaRealizacao,
  erroDoMotivo,
  opcoesDeForma,
  textoDoCancelamento,
  textoDoEstorno,
  valorEditavelNaRealizacao,
  valorNoCampo,
  type AcaoDoLancamento,
} from './formatos.js';

/**
 * As quatro ações da ficha do lançamento — realizar, editar, estornar e
 * cancelar —, no padrão de `CorrecoesDaFatura` (`planos/ModalDePagamento`):
 * os botões, e, ao escolher um, o painel da ação no próprio lugar, com
 * "Voltar".
 *
 * Quem decide QUAIS botões existem é `acoesPermitidas` (o estado da linha
 * e o que o perfil alcança), e isso é cosmético: o servidor confere de novo,
 * e o que ele recusa — 409 com o caminho certo, 400 com o campo — aparece
 * dentro do painel.
 *
 * "Editar" não tem painel aqui: ele reabre `ModalDeLancamento` em modo de
 * edição, e quem troca de modal é a ficha.
 *
 * Depois de qualquer ação: aviso, `['financeiro']` invalidado (o prefixo
 * cobre o resumo, o extrato, as categorias e este detalhe) e a ficha de
 * volta — já com a linha como ficou.
 */

/** O que a ficha está mostrando: ela mesma, a edição, ou o painel de uma ação. */
export type ModoDaAcao = 'ficha' | AcaoDoLancamento;

const ROTULO_DA_ACAO: Readonly<Record<AcaoDoLancamento, string>> = {
  realizar: 'Realizar',
  editar: 'Editar',
  estornar: 'Estornar',
  cancelar: 'Cancelar previsão',
};

const OPCOES_DE_ESCOPO: readonly { valor: EscopoDeCancelamento; rotulo: string }[] = [
  { valor: 'ocorrencia', rotulo: 'Só esta' },
  { valor: 'recorrencia', rotulo: 'Esta e as próximas' },
];

/** O mesmo painel de `CorrecoesDaFatura`: só tokens, para o formulário não parecer parte da ficha. */
const ESTILO_DO_PAINEL: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  padding: 12,
  background: 'var(--bg)',
  border: '1px solid var(--line)',
  borderRadius: 'var(--r-sm)',
};

export function AcoesDoLancamento({
  lancamento,
  acoes,
  hoje,
  modo,
  onEscolher,
  onVoltar,
}: {
  lancamento: DetalheDoLancamento;
  /** `acoesPermitidas`: o estado da linha, já filtrado pelo que o perfil alcança. */
  acoes: readonly AcaoDoLancamento[];
  /** Hoje no fuso da arena — o realizado não passa dele. */
  hoje: string;
  modo: ModoDaAcao;
  onEscolher: (acao: AcaoDoLancamento) => void;
  onVoltar: () => void;
}) {
  if (modo === 'realizar') {
    return <PainelDeRealizacao lancamento={lancamento} hoje={hoje} onVoltar={onVoltar} />;
  }
  if (modo === 'estornar' || modo === 'cancelar') {
    return <PainelDeMotivo acao={modo} lancamento={lancamento} onVoltar={onVoltar} />;
  }
  if (acoes.length === 0) return null;

  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {acoes.map((acao) => (
        <Botao
          key={acao}
          variante={acao === 'realizar' ? 'primario' : 'contorno'}
          pequeno
          onClick={() => onEscolher(acao)}
        >
          {ROTULO_DA_ACAO[acao]}
        </Botao>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------
// Realizar: Previsto → Realizado
// ---------------------------------------------------------------------

/**
 * Data, forma e — no manual — valor do FATO: podem diferir da previsão (o
 * aluguel previsto para o dia 7 foi pago no dia 8, a conta de energia veio
 * R$ 33 maior). Só o que mudou vai ao servidor.
 *
 * A data começa na do lançamento, e nunca no futuro: o realizado é fato, e
 * o servidor recusa data adiante de hoje. O valor do automático vem da
 * origem (outro valor é 400), e o da folha detalhada é a soma dos itens
 * (outro valor é 409) — nos dois o campo aparece, parado, para mostrar o
 * que será gravado.
 *
 * O detalhamento da folha por professor, que o servidor aceita ao realizar,
 * não é pedido aqui: o formulário de "Novo lançamento" também o deixa de
 * fora, e o modelo de remuneração segue em aberto no documento 11.
 */
function PainelDeRealizacao({
  lancamento,
  hoje,
  onVoltar,
}: {
  lancamento: DetalheDoLancamento;
  hoje: string;
  onVoltar: () => void;
}) {
  const avisar = useToast();
  const cliente = useQueryClient();
  const editaValor = valorEditavelNaRealizacao(lancamento);

  const [data, setData] = useState(dataPadraoDaRealizacao(lancamento.data, hoje));
  const [forma, setForma] = useState<FormaPagamento | ''>(lancamento.formaPagamento ?? '');
  const [valor, setValor] = useState(valorNoCampo(lancamento.valor));
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);

  const realizar = useMutation({
    mutationFn: (corpo: RealizacaoDeLancamento) => financeiroApi.realizar(lancamento.id, corpo),
    onSuccess: () => invalidarDinheiro(cliente),
  });

  async function confirmar() {
    setErros({});
    setErroGeral(null);

    const problemas: Record<string, string> = {};
    const erroDaData = erroDaDataDaRealizacao(data, hoje);
    if (erroDaData) problemas['data'] = erroDaData;
    let centavos: number | undefined;
    if (editaValor) {
      const lido = paraCentavos(valor);
      if (lido === null) {
        problemas['valor'] = valor.trim()
          ? 'Valor inválido — use 120 ou 120,50.'
          : 'Informe o valor.';
      } else {
        centavos = lido;
      }
    }
    if (Object.keys(problemas).length > 0) {
      setErros(problemas);
      return;
    }

    // O mesmo schema do servidor: confere o teto do valor e o formato da data.
    const pedido = zRealizacaoDeLancamento.safeParse(
      corpoDaRealizacao(lancamento, { data, valor: centavos, formaPagamento: forma }),
    );
    if (!pedido.success) {
      setErros(errosPorCampo(pedido.error.issues));
      return;
    }

    try {
      await realizar.mutateAsync(pedido.data);
      avisar('Lançamento realizado.');
      onVoltar();
    } catch (erro) {
      if (erro instanceof ErroDeApi && erro.campos) setErros(primeiraMensagem(erro.campos));
      setErroGeral(mensagemDoErro(erro));
    }
  }

  const previstoParaOFuturo = lancamento.data > hoje;
  const ajudaDoValor = editaValor
    ? `Previsto: ${dinheiro(lancamento.valor)}.`
    : lancamento.origemTipo
      ? 'Vem da origem e não muda ao realizar.'
      : 'É a soma do detalhamento por professor.';

  return (
    <form
      noValidate
      style={ESTILO_DO_PAINEL}
      onSubmit={(e) => {
        e.preventDefault();
        void confirmar();
      }}
    >
      <div>
        <b style={{ fontSize: 13 }}>Realizar lançamento</b>
        <p className="sub" style={{ marginTop: 2 }}>
          Diga como o dinheiro realmente se moveu — data, forma e valor podem diferir da previsão.
        </p>
      </div>

      <div className="form f2">
        <Campo
          rotulo="Data"
          ajuda={
            previstoParaOFuturo
              ? `Previsto para ${dataCompleta(lancamento.data)}; o realizado não tem data no futuro.`
              : `Previsto para ${dataCompleta(lancamento.data)}.`
          }
          erro={erros['data']}
        >
          <input
            type="date"
            value={data}
            max={hoje}
            onChange={(e) => setData(e.target.value)}
            autoFocus
          />
        </Campo>
        <Campo rotulo="Forma de pagamento" erro={erros['formaPagamento']}>
          <select value={forma} onChange={(e) => setForma(e.target.value as FormaPagamento | '')}>
            {lancamento.formaPagamento === null && <option value="">Não informada</option>}
            {opcoesDeForma(lancamento.formaPagamento).map((f) => (
              <option key={f} value={f}>
                {ROTULO_FORMA_PAGAMENTO[f]}
              </option>
            ))}
          </select>
        </Campo>
        <Campo rotulo="Valor" ajuda={ajudaDoValor} erro={erros['valor']}>
          <input
            inputMode="decimal"
            value={editaValor ? valor : valorNoCampo(lancamento.valor)}
            onChange={(e) => setValor(e.target.value)}
            disabled={!editaValor}
          />
        </Campo>
      </div>

      {erroGeral && <ErroDaAcao>{erroGeral}</ErroDaAcao>}

      <div style={{ display: 'flex', gap: 8 }}>
        <Botao variante="fantasma" pequeno onClick={onVoltar}>
          Voltar
        </Botao>
        <Botao pequeno type="submit" disabled={realizar.isPending}>
          {realizar.isPending ? 'Realizando…' : 'Confirmar realização'}
        </Botao>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------
// Estornar e cancelar: o motivo
// ---------------------------------------------------------------------

/**
 * Estornar o que foi realizado e cancelar o que estava previsto: as duas
 * pedem o motivo, de 3 a 200 caracteres — o mesmo limite do servidor, lido
 * do schema dele —, e a linha original nunca é apagada.
 *
 * Cancelar uma previsão de recorrência pergunta ainda "quais": só esta
 * ocorrência, ou esta e as próximas (que também encerra a regra). O
 * servidor exige a resposta; o padrão é "só esta", o que desfaz menos.
 */
function PainelDeMotivo({
  acao,
  lancamento,
  onVoltar,
}: {
  acao: 'estornar' | 'cancelar';
  lancamento: DetalheDoLancamento;
  onVoltar: () => void;
}) {
  const avisar = useToast();
  const cliente = useQueryClient();
  const emRecorrencia = acao === 'cancelar' && lancamento.recorrenciaId !== null;

  const [motivo, setMotivo] = useState('');
  const [escopo, setEscopo] = useState<EscopoDeCancelamento>('ocorrencia');
  const [erroDoCampo, setErroDoCampo] = useState<string | null>(null);
  const [erroGeral, setErroGeral] = useState<string | null>(null);

  // Estornar devolve o par (original, estorno); cancelar, quantas previsões saíram.
  const corrigir = useMutation<ResultadoDeEstorno | ResultadoDeCancelamento>({
    mutationFn: () =>
      acao === 'estornar'
        ? financeiroApi.estornar(lancamento.id, { motivo: motivo.trim() })
        : financeiroApi.cancelar(lancamento.id, {
            motivo: motivo.trim(),
            ...(emRecorrencia ? { escopo } : {}),
          }),
    onSuccess: () => invalidarDinheiro(cliente),
  });

  async function confirmar() {
    setErroGeral(null);
    const problema = erroDoMotivo(acao, motivo);
    setErroDoCampo(problema ?? null);
    if (problema) return;

    try {
      const resultado = await corrigir.mutateAsync();
      avisar(
        'cancelados' in resultado
          ? textoDoCancelamento(resultado.cancelados)
          : textoDoEstorno(resultado.estorno),
      );
      onVoltar();
    } catch (erro) {
      setErroGeral(mensagemDoErro(erro));
    }
  }

  return (
    <form
      noValidate
      style={ESTILO_DO_PAINEL}
      onSubmit={(e) => {
        e.preventDefault();
        void confirmar();
      }}
    >
      {emRecorrencia && (
        <Campo
          rotulo="Quais previsões?"
          ajuda={
            escopo === 'ocorrencia'
              ? 'As outras previsões da recorrência continuam como estão.'
              : 'Cancela esta previsão e todas as seguintes, e encerra a recorrência.'
          }
        >
          <Escolhas
            aria-label="Quais previsões cancelar"
            opcoes={OPCOES_DE_ESCOPO}
            valor={escopo}
            onChange={setEscopo}
          />
        </Campo>
      )}

      <Campo
        rotulo={
          acao === 'estornar'
            ? 'Por que estornar este lançamento?'
            : 'Por que a previsão foi cancelada?'
        }
        ajuda={
          acao === 'estornar'
            ? 'O estorno é um lançamento contrário, com a data de hoje: o original continua no extrato e os dois se anulam nas somas.'
            : 'A linha fica registrada, com o motivo, e sai do extrato e das somas.'
        }
        erro={erroDoCampo ?? undefined}
      >
        <input value={motivo} onChange={(e) => setMotivo(e.target.value)} autoFocus />
      </Campo>

      {erroGeral && <ErroDaAcao>{erroGeral}</ErroDaAcao>}

      <div style={{ display: 'flex', gap: 8 }}>
        <Botao variante="fantasma" pequeno onClick={onVoltar}>
          Voltar
        </Botao>
        <Botao pequeno type="submit" disabled={corrigir.isPending}>
          {corrigir.isPending
            ? 'Salvando…'
            : acao === 'estornar'
              ? 'Estornar lançamento'
              : 'Cancelar previsão'}
        </Botao>
      </div>
    </form>
  );
}

/** A recusa do servidor, dentro do painel: 409 com o caminho certo, 400 com o motivo. */
function ErroDaAcao({ children }: { children: React.ReactNode }) {
  return (
    <div className="aviso conflito" role="alert">
      <Icone nome="info" />
      <div>{children}</div>
    </div>
  );
}
